import {
  Box,
  FieldSelect,
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import type { WidgetT } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import {
  Field,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
  SubmissionError,
} from 'redux-form';
import { v4 as uuid } from 'uuid';

import type { FlowChatActionT } from 'components/powerChatFlow/api/types';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { withFlows, withQueues, withWidgets } from 'hocs';
import type {
  WithFlowsInjectedPropsT,
  WithQueuesInjectedProps,
  WithWidgetsInjectedPropsT,
} from 'hocs';
import { FlowCreatePayloadT } from 'hocs/flows/api/types';
import { useNotifications } from 'hooks/useNotifications';
import { validateName } from 'utils/formValidator';

type AddWidgetModalOwnPropsT = {
  goToFlow: (flowId: string) => void;
  isOpen: boolean;
  onClose: () => void;
};

type AddWidgetModalFormValuesT = {
  name: string;
  queue: string;
};

type PropsT = AddWidgetModalOwnPropsT &
  WithFlowsInjectedPropsT<FlowChatActionT> &
  WithQueuesInjectedProps &
  WithWidgetsInjectedPropsT &
  InjectedFormProps<
    AddWidgetModalFormValuesT,
    AddWidgetModalOwnPropsT,
    FormErrors<AddWidgetModalFormValuesT>
  >;

const MAX_LENGTH = 255;

const validate = (values: AddWidgetModalFormValuesT) => {
  const errors: FormErrors<AddWidgetModalFormValuesT> = {};

  if (!values.queue) {
    errors.queue = 'Please select a queue';
  }

  return errors;
};

const AddWidgetModalImpl = (props: PropsT): JSX.Element => {
  const notify = useNotifications();

  const handleFormSubmit = async (
    values: AddWidgetModalFormValuesT,
  ): Promise<void> => {
    ampli.chatFormSubmitted({ mode: 'create' });

    const { widgets } = props;
    const { name, queue } = values;

    return widgets
      .create({
        queue_id: queue,
        name,
      })
      .then((res) => {
        notify({
          title: `Widget ${name}`,
          text: `"${name}" widget has been created`,
          icon: 'done',
          className: 'success',
          time: 5000,
        });

        handleCreateFlow(res.data, queue);
      })
      .catch(() => {
        notify({
          title: 'An error occurred',
          text: `An error occurred when trying to create the "${name}" widget`,
          icon: 'exclamation',
          className: 'danger',
        });

        throw new SubmissionError({
          name: 'Widget could not be created',
        });
      });
  };

  const handleCreateFlow = async (widget: WidgetT, queueId: string) => {
    const { flows, goToFlow, onClose } = props;
    const queue = await handleGetQueue(queueId);

    if (queue) {
      const initNodeId = uuid();
      const node2ndId = uuid();
      const node3rdId = uuid();

      const payload: FlowCreatePayloadT<FlowChatActionT> = {
        actions: [
          {
            badges: [],
            childIds: [],
            connectionLabel: 'arrow',
            icon: null,
            id: initNodeId,
            isAction: false,
            isEditable: false,
            isRemovable: false,
            nodeType: FlowNodeTypes.INIT,
            overlay: [],
            pageX: 0,
            pageY: 0,
            parentId: null,
            payload: null,
            text: 'Incoming chat',
          },
          {
            badges: [],
            childIds: [],
            connectionLabel: 'arrow',
            icon: 'i-widget-customize',
            id: node2ndId,
            isAction: false,
            isEditable: true,
            isRemovable: true,
            nodeType: FlowNodeTypes.WIDGET_CUSTOMIZATION,
            overlay: [],
            pageX: 0,
            pageY: 150,
            parentId: initNodeId,
            payload: {
              localizations: {
                'en-US': {
                  minimized: {
                    html_text_online: 'Need help? **We are online**',
                    html_text_offline: 'Get in touch',
                  },
                },
              },
              medium: {
                chatbox_view: {
                  position: 'bottom_right',
                },
                minimized_view: {
                  position: 'bottom_right',
                },
              },
              small: {
                chatbox_view: {
                  position: 'bottom_right',
                },
                minimized_view: {
                  position: 'bottom_right',
                },
              },
              theme: {
                color: '#6474ba',
                color_darken: '#3e4c8b',
                color_lighten: '#9fa9d4',
                text_color: '#ffffff',
              },
              nextNodeId: node3rdId,
            },
            text: 'Widget Customization',
          },
          {
            badges: [],
            childIds: [],
            connectionLabel: 'arrow',
            icon: 'i-queue',
            id: node3rdId,
            isAction: false,
            isEditable: true,
            isRemovable: true,
            nodeType: FlowNodeTypes.QUEUE,
            overlay: [],
            pageX: 0,
            pageY: 300,
            parentId: node2ndId,
            payload: {
              queue: {
                value: queue.id,
                label: queue.name,
              },
            },
            text: `Queue (${queue.name})`,
          },
        ],
        active: true,
        channel: 'widget_chat',
        location: widget.id,
        name: widget.name,
        number: widget.id,
      };

      flows
        .create(payload)
        .then((res) => {
          onClose();
          goToFlow(res.data.id);
        })
        .catch(() => {
          notify({
            title: 'An error occurred',
            text: `An error occurred when trying to create flow`,
            icon: 'exclamation',
            className: 'danger',
          });
        });
    }
  };

  const handleGetQueue = (queueId: string) => {
    const { queues } = props;

    return queues
      .getQueue(queueId)
      .then((res) => {
        return res.data;
      })
      .catch(() => {
        notify({
          title: 'An error occurred',
          text: `An error occurred when trying to fetch queue`,
          icon: 'exclamation',
          className: 'danger',
        });
      });
  };

  const handleQueueSearch = (query: string) => {
    props.queues.searchTermChange(query);
  };

  const handeQueueSearchToggle = (open?: boolean) => {
    if (open) {
      props.queues.searchTermChange('');
    }
  };

  const handleCancel = () => {
    ampli.chatFormCanceled({ mode: 'create' });
    props.onClose();
  };

  const { handleSubmit, isOpen, queues } = props;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel}>
      <Box id="add-chat-widget-modal" width="600px">
        <ModalHead onClose={handleCancel} title="Add a new chat widget" />
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalBody>
            <Field
              component={FieldText}
              label="Name"
              maxLength={MAX_LENGTH}
              name="name"
              placeholder="Widget name"
              required
              type="text"
              validate={validateName}
            />
            <Field
              component={FieldSelect}
              items={queues.items}
              label="Queue"
              loading={queues.getItemsAsyncStatus === 'loading'}
              name="queue"
              placeholder="Please select a queue"
              required
              searchable
              onSearch={handleQueueSearch}
              onToggle={handeQueueSearchToggle}
              pagination={{
                infinite: true,
                nextEnable: queues.hasNextPage,
                onNext: queues.nextPage,
              }}
            />
          </ModalBody>
          <ModalFoot
            cancelOnClick={handleCancel}
            confirmName="Add widget"
            confirmOnClick={handleSubmit(handleFormSubmit)}
            confirmProps={{ type: 'submit' }}
          />
        </Form>
      </Box>
    </Modal>
  );
};

export const AddWidgetModal = reduxForm<
  AddWidgetModalFormValuesT,
  AddWidgetModalOwnPropsT,
  FormErrors
>({
  enableReinitialize: true,
  form: 'createWidgetForm',
  touchOnBlur: false,
  validate,
})(
  withQueues(
    withWidgets(
      withFlows<FlowChatActionT, PropsT>(AddWidgetModalImpl, {
        initialParams: {
          channel: 'widget_chat',
        },
      }),
    ),
    { infinite: true },
  ),
);
